import type { MeResponse } from '@hefesto/contracts';

/**
 * "La interfaz oculta, el backend decide" (docs/DESIGN-SYSTEM.md §6.2). Estas reglas SOLO deciden
 * qué se muestra; cada endpoint vuelve a autorizar. Fuente: `/api/v1/me` (cargado de la base en
 * cada petición). Nunca se muestra un botón que luego fallaría con 403:
 *   - `hidden`: el rol no tiene el permiso → no se ofrece la acción;
 *   - `mfa`: tiene el permiso, pero es de riesgo alto y la sesión no superó el segundo factor →
 *     se ofrece guiando a verificar/enrolar el MFA (nunca un botón que falle);
 *   - `allowed`: se puede hacer.
 */
export type ActionState = 'allowed' | 'mfa' | 'hidden';

export function isInternal(me: MeResponse): boolean {
  return me.platform?.status === 'active';
}

export function hasPlatform(me: MeResponse, permission: string): boolean {
  return isInternal(me) && (me.platform?.permissions.includes(permission) ?? false);
}

export function actionState(me: MeResponse, permission: string): ActionState {
  if (!hasPlatform(me, permission)) return 'hidden';
  if (me.platform?.mfa.requiredFor.includes(permission) && !me.session.mfaVerified) return 'mfa';
  return 'allowed';
}

/** El miembro interno tiene permisos de riesgo alto y todavía no puede usarlos. */
export function mfaPending(me: MeResponse): { needed: boolean; enrolled: boolean } {
  const required = (me.platform?.mfa.requiredFor.length ?? 0) > 0;
  return {
    needed: isInternal(me) && required && !me.session.mfaVerified,
    enrolled: me.platform?.mfa.enrolled ?? false,
  };
}

/** Destino para verificar el MFA y volver a donde estaba. */
export function mfaHref(next?: string): string {
  return next ? `/admin/security?next=${encodeURIComponent(next)}` : '/admin/security';
}

/** Quién ve el catálogo de planes (mismo criterio que la API). */
export const PLAN_READERS = [
  'platform.plans.manage',
  'platform.clients.modules.manage',
  'platform.billing.view',
] as const;

/** (02B) Quién ve la página de accesos temporales: quien los pide o quien los aprueba. */
export const ACCESS_READERS = [
  'platform.clients.data.access',
  'platform.clients.data.approve',
] as const;

/** (02B) Quién ve Sistema: salud y jobs, o configuración de canales de aviso. */
export const SYSTEM_READERS = [
  'platform.system.health.view',
  'platform.notifications.manage',
] as const;

/** (02B) "Nadie concede más de lo que tiene": ¿todos estos permisos están entre los míos? */
export function canHandle(me: MeResponse, permissions: readonly string[]): boolean {
  const mine = new Set(me.platform?.status === 'active' ? me.platform.permissions : []);
  return permissions.every((p) => mine.has(p));
}
